import { useQuery } from '@tanstack/react-query';
import { sessionState } from '@/app/sessionState';
import { fetchData } from './fetchData';
import { mapAPIUrl, paramsToUrl } from '@/utils';
import { qkeyFromUrl } from '@/utils';
import { relativeDateReviver } from '@/utils';

/**
 * A generic hook to fetch data from any url using React Query.
 *
 * @param {string} APIRequest - The API url to fetch data from.
 * @param {object} options - Optional React Query options. 
 */
export function useAPIQuery(APIRequest, options = {}) {

  let url = '';
  let parameters = undefined;
  let queryKey = [];
  let enabled = true;
  let CRUDMode = false;
  let designerDemoData = false;

  if (typeof APIRequest === 'string') {
    url = APIRequest
  } else if (APIRequest.url) {
    url = APIRequest.url;
    parameters = APIRequest.parameters;
  } else if (APIRequest?.sampleData) {
    url = APIRequest.sampleData;
  }

  // console.log('useAPIQuery', url, parameters, sessionState.demoData.enabled);

  if (sessionState.demoData.enabled && APIRequest?.sampleData) {
    console.warn("useAPIQuery: using demo data");
    url = APIRequest.sampleData;
    parameters = undefined;
  }

  if (parameters) {
    queryKey = [qkeyFromUrl(url), parameters];
    url = paramsToUrl(url, parameters);
  } else {
    queryKey = [url];
  }

  if (!url) {
    enabled = false;
  } else {
    if (APIRequest.enabled === false) {
      enabled = false;
    } else if (typeof APIRequest.enabled === 'string' && APIRequest.parameters) {
      enabled = !!APIRequest.parameters[APIRequest.enabled];
      CRUDMode = !enabled;
    }
  }


  if (sessionState.demoData.enabled && (sessionState.demoData.url == url || !sessionState.demoData.url) && sessionState.demoData.data != "{}") {
    enabled = false;
    designerDemoData = true;
  }

  //console.log('useAPIQuery', url, parameters, queryKey, enabled);

  let queryResult = useQuery({
    queryFn: (context) => fetchData(url, context),
    retry: 1,
    enabled: enabled,
    queryKey: queryKey,
    refetchOnWindowFocus: false,
    ...options, // Allow overriding default options
    staleTime: 30 * 1000, // Consider data fresh for 10 seconds
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });


  // CRUDMode returns an empty response for create forms when id parameter is empty

  if (CRUDMode || designerDemoData) {
    queryResult = {
      data: designerDemoData ? JSON.parse(sessionState.demoData.data, relativeDateReviver) : {},
      error: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      isSuccess: true,
      isIdle: false,
      status: "success",
      fetchStatus: "idle",
      refetch: Function, // refetch function
      remove: Function, // remove query from cache function
      fetchNextPage: Function, // for infinite queries
      fetchPreviousPage: Function, // for infinite queries
      hasNextPage: false, // for infinite queries
      hasPreviousPage: false, // for infinite queries
      isFetchingNextPage: false, // for infinite queries
      isFetchingPreviousPage: false, // for infinite queries
      dataUpdatedAt: Date.now(),
      errorUpdatedAt: 0,
      isPlaceholderData: false,
      isPreviousData: false,
      isRefetchError: false,
      isStale: true
    }
  }

  return queryResult

}
